import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarCheck, ArrowDown, ShieldCheck, Clock3 } from "lucide-react";
import { IMAGES, WA_SCHEDULE } from "@/lib/constants";

const LINES = ["Mais que", "tratamento,", "qualidade de vida."];
const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-white pt-[76px]"
    >
      <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-cob-beige/70 blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 -left-56 h-[420px] w-[420px] rounded-full bg-cob-light/50 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center min-h-[calc(100vh-76px)] py-14 lg:py-10">
          <div className="lg:col-span-6 xl:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 18, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              className="inline-flex items-center gap-2.5 rounded-full border border-cob-gold/40 bg-cob-beige/60 px-4 py-2"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-cob-gold animate-pulse" />
              <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-cob-dark">
                Desde 2004 • Teresina — PI
              </span>
            </motion.div>

            <h1
              data-testid="hero-title"
              className="mt-7 font-serif font-semibold tracking-tight text-cob-dark text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-[4.2rem]"
            >
              {LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === 2 ? "italic text-cob-green" : ""}`}
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.95, delay: 0.25 + i * 0.14, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
              className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-cob-muted"
            >
              Ortopedia, fisioterapia e cuidado especializado para você se movimentar e viver
              melhor.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-4 flex items-center gap-3"
            >
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
                className="h-px w-10 origin-left bg-cob-gold"
              />
              <p className="text-sm font-medium text-cob-dark/80">Cuidando da sua saúde desde 2004.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
              className="mt-9 flex flex-col sm:flex-row gap-3.5"
            >
              <a
                href={WA_SCHEDULE}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="btn-press inline-flex items-center justify-center gap-2.5 rounded-full bg-cob-green px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-cob-dark hover:shadow-[0_16px_35px_-12px_rgba(6,63,50,0.55)]"
              >
                <CalendarCheck className="h-4.5 w-4.5" />
                Agende sua consulta
              </a>
              <a
                href="#clinica"
                data-testid="hero-cta-clinica"
                className="btn-press group inline-flex items-center justify-center gap-2.5 rounded-full border border-cob-dark/20 bg-white px-8 py-4 text-sm font-semibold text-cob-dark transition-all duration-300 hover:border-cob-gold hover:text-cob-green"
              >
                Conheça a Clínica
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 1.15 }}
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] text-cob-muted"
            >
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-cob-gold" /> Atendimento especializado
              </span>
              <span className="inline-flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-cob-gold" /> Mais de 20 anos de experiência
              </span>
            </motion.div>
          </div>

          <div className="lg:col-span-6 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, delay: 0.45, ease: EASE }}
              className="relative mx-auto max-w-[520px]"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.9, delay: 1.1 }}
                className="absolute -inset-3 rounded-[2.2rem] border border-cob-gold/40 translate-x-4 translate-y-4 pointer-events-none"
              />
              <motion.div
                initial={{ clipPath: "inset(10% 10% 10% 10% round 2rem)" }}
                animate={{ clipPath: "inset(0% 0% 0% 0% round 2rem)" }}
                transition={{ duration: 1.3, delay: 0.55, ease: EASE }}
                className="relative overflow-hidden rounded-[2rem] shadow-[0_35px_80px_-30px_rgba(6,63,50,0.45)]"
              >
                <motion.img
                  src={IMAGES.care}
                  alt="Atendimento humanizado na Clínica COB"
                  data-testid="hero-image"
                  style={{ y: imageY }}
                  initial={{ scale: 1.25 }}
                  animate={{ scale: 1.12 }}
                  transition={{ duration: 1.6, delay: 0.55, ease: EASE }}
                  className="h-[420px] sm:h-[520px] lg:h-[560px] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-cob-dark/35 via-transparent to-transparent" />
              </motion.div>

              <motion.div style={{ y: badgeY }} className="absolute -bottom-6 -left-4 sm:-left-8">
                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
                  className="rounded-2xl bg-white/95 backdrop-blur-md border border-cob-light px-5 py-4 shadow-[0_18px_45px_-18px_rgba(6,63,50,0.4)]"
                >
                  <motion.div
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
                  >
                    <p className="font-serif text-3xl font-semibold text-cob-dark leading-none">2004</p>
                    <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-cob-muted">
                      Cuidando de você
                    </p>
                  </motion.div>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.9, delay: 1.45, ease: EASE }}
                className="absolute top-6 -right-3 sm:-right-6"
              >
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 0.8 }}
                  className="rounded-2xl bg-cob-dark px-5 py-4 shadow-xl"
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cob-gold">
                    Zona Norte
                  </p>
                  <p className="mt-1 text-sm font-medium text-white">Teresina — PI</p>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
